import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Progress } from '../../../ui/progress';
import { MetricCard, MetricLabel, MetricValue } from '../../../ui/metric-card';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { LOCATION_BREAKDOWN, CAMPAIGNS } from '../../fixtures';
import { Mail, Loader2, CheckCircle2, Users, Clock, TrendingUp, Info } from 'lucide-react';

const Timeline = () => (
  <Card>
    <CardHeader>
      <CardTitle className='text-base'>Recent activity</CardTitle>
    </CardHeader>
    <CardContent className='p-0'>
      <ol className='relative'>
        {[
          { when: 'Today, 10:42', label: 'Reminder sent to 157 outstanding invitations', icon: Mail },
          { when: 'Yesterday, 16:08', label: '12 new responses received', icon: Users },
          { when: '2 days ago', label: 'Dashboard refreshed — response rate crossed 50%', icon: TrendingUp },
          { when: '3 days ago', label: 'Campaign went live — 392 invitations sent', icon: CheckCircle2 },
        ].map((item, i) => {
          const Icon = item.icon;
          return (
            <li key={i} className='flex gap-3 px-5 py-3 border-b border-border last:border-b-0'>
              <div className='w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-accent flex-shrink-0'>
                <Icon size={14} strokeWidth={1.5} />
              </div>
              <div>
                <div className='text-sm'>{item.label}</div>
                <div className='text-xs text-muted-foreground'>{item.when}</div>
              </div>
            </li>
          );
        })}
      </ol>
    </CardContent>
  </Card>
);

const DashboardTab = ({ campaign, onRemindClick }) => {
  const isClosing = campaign.status === 'closing';
  const daysRemaining = campaign.deadline
    ? Math.max(
        0,
        Math.ceil((new Date(campaign.deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
      )
    : null;

  return (
    <div className='space-y-6'>
      {isClosing && (
        <Alert>
          <Loader2 size={14} strokeWidth={1.5} className='animate-spin' />
          <AlertTitle>Calculating results</AlertTitle>
          <AlertDescription>
            Applying DEFRA emission factors for {campaign.reportingPeriod} to {campaign.completedCount} respondents
            across {LOCATION_BREAKDOWN.length} locations. This usually takes under 2 minutes.
          </AlertDescription>
        </Alert>
      )}

      <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
        <MetricCard accent='default'>
          <MetricLabel>Response rate</MetricLabel>
          <MetricValue unit='%'>{campaign.responseRate}</MetricValue>
          <div className='mt-3'>
            <Progress value={campaign.responseRate} />
          </div>
          <div className='text-xs text-muted-foreground mt-2'>
            {campaign.completedCount} of {campaign.invitedCount} completed
          </div>
        </MetricCard>

        <MetricCard accent='success'>
          <MetricLabel>Completed</MetricLabel>
          <MetricValue>{campaign.completedCount}</MetricValue>
          <div className='text-xs text-success flex items-center gap-1.5 mt-2'>
            <TrendingUp size={12} strokeWidth={1.5} />
            +24 in the last 48h
          </div>
        </MetricCard>

        <MetricCard accent='amber'>
          <MetricLabel>Outstanding</MetricLabel>
          <MetricValue>{campaign.invitedCount - campaign.completedCount}</MetricValue>
          <div className='text-xs text-muted-foreground flex items-center gap-1.5 mt-2'>
            <Users size={12} strokeWidth={1.5} />
            eligible for reminders
          </div>
        </MetricCard>

        <MetricCard>
          <MetricLabel>Deadline</MetricLabel>
          <MetricValue>{daysRemaining !== null ? daysRemaining : '—'}</MetricValue>
          <div className='text-xs text-muted-foreground flex items-center gap-1.5 mt-2'>
            <Clock size={12} strokeWidth={1.5} />
            {daysRemaining !== null ? 'days remaining' : 'no deadline set'}
          </div>
        </MetricCard>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6'>
        <Card>
          <CardHeader className='flex-row items-center justify-between space-y-0'>
            <div>
              <CardTitle className='text-base'>Response rate by office location</CardTitle>
              <p className='text-xs text-muted-foreground mt-1'>
                Each location is extrapolated independently to its own FTE (PRD §5c.5).
              </p>
            </div>
            {!isClosing && (
              <Button size='sm' variant='outline' onClick={onRemindClick}>
                <Mail size={14} strokeWidth={1.5} />
                Send reminders
              </Button>
            )}
          </CardHeader>
          <CardContent className='space-y-5'>
            {LOCATION_BREAKDOWN.map((row) => {
              const responseRate = Math.round((row.respondents / row.fte) * 100);
              const outstanding = row.fte - row.respondents;
              return (
                <div key={row.location}>
                  <div className='flex items-end justify-between mb-1.5'>
                    <div>
                      <div className='font-medium text-[14px]'>{row.location}</div>
                      <div className='text-xs text-muted-foreground'>
                        {row.respondents} of {row.fte} FTE · {outstanding} outstanding
                      </div>
                    </div>
                    <div className='text-right'>
                      <div className='font-mono text-base tabular-nums'>{responseRate}%</div>
                      <Badge
                        variant={responseRate >= 70 ? 'success' : responseRate >= 50 ? 'info' : 'warning'}
                        size='sm'>
                        {responseRate >= 70 ? 'On track' : responseRate >= 50 ? 'Progressing' : 'Needs push'}
                      </Badge>
                    </div>
                  </div>
                  <Progress value={responseRate} />
                </div>
              );
            })}
          </CardContent>
        </Card>

        <div className='space-y-4'>
          <Timeline />
          {campaign.status === 'active' && (
            <Alert>
              <Info size={14} strokeWidth={1.5} />
              <AlertTitle>Auto-reminders are on</AlertTitle>
              <AlertDescription>
                We will nudge outstanding invitations 7 days and 2 days before {campaign.deadline ? 'the deadline' : 'closing'}.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
